import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/Spinner';
import type { BookingCalendarMember } from '../../types/appointments';
import {
  addCalendarMember,
  removeCalendarMember,
  updateCalendarMember,
  verifyCalendarMember,
} from './api';

// The staff connected to a booking calendar.
//
// ⚠️⚠️ EVERY ROW HAS A "TEST CONNECTION" BUTTON, AND THAT IS THE POINT OF THIS
// SECTION. A calendar that was never shared with us — or whose share was revoked
// — comes back from Google as an ERROR, not as an empty diary, and the engine
// therefore treats that person as unavailable rather than free. That is the right
// call (the alternative books the bot over their whole week) but it is SILENT: no
// slots for that salesperson, no explanation. This is where a human finds out
// why, before a customer does.
//
// So a member who has never verified, or whose last check failed, wears a
// warning badge. "No news" is not treated as good news.

function relative(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 'recently';
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function VerifyBadge({ member }: { member: BookingCalendarMember }) {
  if (member.lastVerifyError) {
    return (
      <Badge variant="danger" title={member.lastVerifyError}>
        <AlertTriangle className="mr-1 h-3 w-3" />
        Cannot read this calendar
      </Badge>
    );
  }
  if (!member.lastVerifiedAt) {
    // ⚠️ NEVER-CHECKED IS ITS OWN STATE, not a quiet pass. Until a real
    // free/busy read succeeds we do not know the share exists, and this person
    // will be excluded from every slot.
    return (
      <Badge variant="warning">
        <AlertTriangle className="mr-1 h-3 w-3" />
        Not checked yet
      </Badge>
    );
  }
  return (
    <Badge variant="success">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      Checked {relative(member.lastVerifiedAt)}
    </Badge>
  );
}

export function StaffSection({
  calendarId,
  members,
  serviceAccountEmail,
  calendarConfigured,
}: {
  calendarId: string;
  members: BookingCalendarMember[];
  serviceAccountEmail: string | null;
  calendarConfigured: boolean;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [googleCalendarId, setGoogleCalendarId] = useState('');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['booking-calendar', calendarId] });
    void queryClient.invalidateQueries({ queryKey: ['booking-availability', calendarId] });
  };

  const add = useMutation({
    mutationFn: () =>
      addCalendarMember(calendarId, {
        displayName: displayName.trim(),
        googleCalendarId: googleCalendarId.trim(),
      }),
    onSuccess: () => {
      setDisplayName('');
      setGoogleCalendarId('');
      invalidate();
      toast.success('Staff member connected. Test the connection to confirm the calendar is shared.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const patch = useMutation({
    mutationFn: (args: { memberId: string; enabled: boolean }) =>
      updateCalendarMember(calendarId, args.memberId, { enabled: args.enabled }),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => removeCalendarMember(calendarId, memberId),
    onSuccess: () => {
      invalidate();
      toast.success('Staff member disconnected.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const verify = useMutation({
    mutationFn: (memberId: string) => verifyCalendarMember(calendarId, memberId),
    onSuccess: (result) => {
      invalidate();
      if (result.ok) toast.success('We can read that calendar.');
      // ⚠️ The failure is not a toast that vanishes — it is stored on the row and
      // rendered under it, because the person who has to act on it may not be
      // the person who pressed the button.
      else toast.error(result.error ?? 'We could not read that calendar.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const canAdd = displayName.trim() !== '' && googleCalendarId.trim().length > 2;

  return (
    <div className="space-y-4">
      {!calendarConfigured && (
        // ⚠️ Said before anyone presses Test connection, not after it fails for a
        // reason nothing on the page explains.
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          The Kyck calendar service account has not been set up on this environment yet, so we
          cannot read anyone&apos;s calendar. Staff can be added now, but every check will fail
          until it is configured.
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        The salespeople this calendar can book. We read their free/busy to find a time — so the
        calendar has to be shared with Kyck first.
        {serviceAccountEmail && (
          <>
            {' '}
            Share it with{' '}
            <span className="font-medium text-foreground">{serviceAccountEmail}</span>. &ldquo;See
            only free/busy&rdquo; is enough to find times; &ldquo;Make changes to events&rdquo; is
            needed for us to put the booking on it.
          </>
        )}
      </p>

      {members.length === 0 ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-muted-foreground">
          Nobody is connected yet, so this calendar cannot offer any times.
        </p>
      ) : (
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
            >
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={member.enabled}
                  onCheckedChange={(checked) =>
                    patch.mutate({ memberId: member.id, enabled: checked === true })
                  }
                  aria-label={`${member.displayName} bookable`}
                />
                <span className="font-medium">{member.displayName}</span>
              </label>
              <span className="text-xs text-muted-foreground">{member.googleCalendarId}</span>
              {!member.enabled && <Badge variant="neutral">Not bookable</Badge>}
              <div className="ml-auto flex items-center gap-2">
                <VerifyBadge member={member} />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={verify.isPending}
                  onClick={() => verify.mutate(member.id)}
                >
                  {verify.isPending && verify.variables === member.id ? (
                    <Spinner className="mr-1 h-3 w-3" />
                  ) : null}
                  Test connection
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Disconnect ${member.displayName}`}
                  onClick={() => remove.mutate(member.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {member.lastVerifyError && (
                <p className="w-full text-xs text-rose-700">{member.lastVerifyError}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-dashed border-slate-300 p-3">
        <div>
          <Label htmlFor="member-name">Name</Label>
          <Input
            id="member-name"
            className="mt-1 w-48"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Bob Smith"
          />
        </div>
        <div>
          <Label htmlFor="member-calendar">Calendar address</Label>
          <Input
            id="member-calendar"
            className="mt-1 w-72"
            value={googleCalendarId}
            onChange={(e) => setGoogleCalendarId(e.target.value)}
            placeholder="bob@dealership.com.au"
          />
        </div>
        <Button type="button" disabled={!canAdd || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? <Spinner className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
          Connect
        </Button>
        <p className="w-full text-xs text-muted-foreground">
          Usually their email address. A shared appointments calendar has a longer address ending
          in <code>@group.calendar.google.com</code>.
        </p>
      </div>
    </div>
  );
}
